import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../../utils/api';
import { WiDaySunny, WiCloudy, WiDayCloudy, WiRain, WiThermometer } from 'react-icons/wi';
import { getCardinalFromDegrees } from '../../map/utils/windUtils';
import safeStorage from '../../../utils/safeStorage';

const numberOrNull = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : null);

const Slider = ({ label, min, max, step = 0.01, value, onChange, format = (x) => x }) => (
  <div className="mb-3">
    <div className="d-flex justify-content-between align-items-center">
      <label className="form-label mb-1" style={{ fontWeight: 600 }}>{label}</label>
      <span>{format(value)}</span>
    </div>
    <input
      type="range"
      className="form-range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  </div>
);

export default function ForecastSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [weights, setWeights] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/forecast-settings/');
      setWeights(res?.data || {});
    } catch (e) {
      setError(e?.response?.status === 403 ? 'Not authorized' : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateField = (key) => (v) => setWeights((w) => ({ ...(w || {}), [key]: v }));

  const save = useCallback(async () => {
    if (!weights) return;
    setSaving(true);
    setError(null);
    try {
      await api.put('/forecast-settings/', weights);
      try { safeStorage.setItem('yona_forecast_settings_bumped_at', String(Date.now())); } catch (_) {}
    } catch (e) {
      setError('Save failed');
    } finally {
      setSaving(false);
    }
  }, [weights]);

  const formatPercent = (v) => `${Math.round(v * 100)}%`;
  const formatPoints = (v) => `${Math.round(v * 100)} pts`;

  // --- Preview scoring (client-side mirror of backend logic) ---
  const computePreviewScore = useCallback((w, s) => {
    // Weather subscores
    const tempDrop = Math.max(0, (s.prev_temp_day ?? 0) - (s.temp_day ?? 0));
    const tempScore = Math.min(1, tempDrop / 20);
    const pressureDelta = Math.abs((s.pressure ?? 0) - (s.prev_pressure ?? 0));
    const pressureScore = Math.min(1, pressureDelta / 10);
    const windScore = Math.max(0, Math.min(1, 1 - ((s.wind_speed ?? 0) / 20)));

    // Minor weight increases with strong weather
    const avgWeather = (tempScore + pressureScore + windScore) / 3;
    const minorWeight = 0.5 + 0.25 * avgWeather; // 0.5..0.75

    // Lunar overlap minutes (assume full-daylight windows for simplicity)
    const majorOverlap = (s.majors_daylight_count ?? 0) * 120; // minutes
    const minorOverlap = (s.minors_daylight_count ?? 0) * 60;   // minutes

    const weightedOverlap = majorOverlap + minorWeight * minorOverlap;
    const possibleWeighted = 2 * 120 + 2 * 60 * minorWeight;
    let lunarScore = possibleWeighted > 0 ? Math.min(1, Math.max(0, weightedOverlap / possibleWeighted)) : 0;

    // Bonus: any daylight major
    if ((s.majors_daylight_count ?? 0) > 0) {
      lunarScore = Math.min(1, lunarScore + (w.major_bonus ?? 0));
    }
    // Major + Minor combo bonus (any daylight overlap of both)
    if (majorOverlap > 0 && minorOverlap > 0) {
      lunarScore = Math.min(1, lunarScore + (w.combo_bonus ?? 0));
    }
    // Crepuscular major bonus
    if (s.major_crepuscular_minutes && s.major_crepuscular_minutes >= 30) {
      lunarScore = Math.min(1, lunarScore + (w.crepuscular_major_bonus ?? 0));
    }
    // Crepuscular minor bonus
    if (s.minor_crepuscular_minutes && s.minor_crepuscular_minutes >= 30) {
      lunarScore = Math.min(1, lunarScore + (w.crepuscular_minor_bonus ?? 0));
    }

    // Base weighted sum
    let final = 100 * (
      (w.lunar ?? 0.4) * lunarScore
      + (w.temp ?? 0.3) * tempScore
      + (w.pressure ?? 0.2) * pressureScore
      + (w.wind ?? 0.1) * windScore
    );

    // Final adjustments
    if (s.weekday !== undefined) {
      if ([0,1,2,3].includes(s.weekday)) final += 100 * (w.midweek_bonus_final ?? 0);
      if ([5,6].includes(s.weekday)) final -= 100 * (w.weekend_penalty_final ?? 0);
    }
    const thr = w.cool_day_threshold_f ?? 60;
    const effectiveTemp = (typeof s.temp_max === 'number') ? s.temp_max : s.temp_day;
    if ((effectiveTemp ?? 999) <= thr) final += 100 * (w.cool_day_bonus_final ?? 0);
    if (s.is_rainy) final -= 100 * (w.rain_penalty_final ?? 0);
    if (s.wind_is_optimal) final += 100 * (w.optimal_wind_match_bonus_final ?? 0);

    return Math.round(Math.max(0, Math.min(100, final)));
  }, []);

  // Example scenarios
  const scenarios = useMemo(() => ([
    {
      key: 'ideal',
      title: 'Ideal Day',
      weather_type: 'sun',
      temp_min: 45, temp_max: 55, wind_speed: 6, wind_deg: 210,
      majors_daylight_count: 1, minors_daylight_count: 2, major_crepuscular_minutes: 60, minor_crepuscular_minutes: 60,
      prev_temp_day: 72, temp_day: 55, prev_pressure: 1011, pressure: 1017,
      weekday: 2, is_rainy: false,
    },
    {
      key: 'medium',
      title: 'Middle Ground',
      weather_type: 'partly_cloudy',
      temp_min: 58, temp_max: 64, wind_speed: 12, wind_deg: 150,
      majors_daylight_count: 0, minors_daylight_count: 1, major_crepuscular_minutes: 0,
      prev_temp_day: 68, temp_day: 63, prev_pressure: 1014, pressure: 1017,
      weekday: 0, is_rainy: false,
    },
    {
      key: 'cool_minor',
      title: 'Cool With Minor',
      weather_type: 'cloudy',
      temp_min: 50, temp_max: 59, wind_speed: 8, wind_deg: 300,
      majors_daylight_count: 0, minors_daylight_count: 1, minor_crepuscular_minutes: 40,
      prev_temp_day: 64, temp_day: 59, prev_pressure: 1013, pressure: 1019,
      weekday: 3, is_rainy: false,
    },
    {
      key: 'warm_windy_weekend',
      title: 'Warm & Windy (Weekend)',
      weather_type: 'sun',
      temp_min: 68, temp_max: 75, wind_speed: 18, wind_deg: 20,
      majors_daylight_count: 0, minors_daylight_count: 0,
      prev_temp_day: 72, temp_day: 75, prev_pressure: 1015, pressure: 1015,
      weekday: 6, is_rainy: false,
    },
    {
      key: 'terrible',
      title: 'Terrible Day',
      weather_type: 'rainy',
      temp_min: 68, temp_max: 72, wind_speed: 18, wind_deg: 30,
      majors_daylight_count: 0, minors_daylight_count: 0, major_crepuscular_minutes: 0,
      prev_temp_day: 72, temp_day: 72, prev_pressure: 1015, pressure: 1015,
      weekday: 6, is_rainy: true,
    },
    {
      key: 'post_front_midweek',
      title: 'Post-Front Midweek',
      weather_type: 'sun',
      temp_min: 40, temp_max: 52, wind_speed: 7, wind_deg: 240,
      majors_daylight_count: 1, minors_daylight_count: 1, major_crepuscular_minutes: 40,
      prev_temp_day: 70, temp_day: 52, prev_pressure: 1008, pressure: 1018,
      weekday: 1, is_rainy: false,
    },
    {
      key: 'one_major_only',
      title: '1 Major, 0 Minor',
      weather_type: 'partly_cloudy',
      temp_min: 52, temp_max: 61, wind_speed: 9, wind_deg: 200,
      majors_daylight_count: 1, minors_daylight_count: 0, major_crepuscular_minutes: 20,
      prev_temp_day: 66, temp_day: 61, prev_pressure: 1012, pressure: 1018,
      weekday: 4, is_rainy: false,
    },
    {
      key: 'two_minors_only',
      title: '0 Major, 2 Minors',
      weather_type: 'cloudy',
      temp_min: 55, temp_max: 63, wind_speed: 10, wind_deg: 280,
      majors_daylight_count: 0, minors_daylight_count: 2, minor_crepuscular_minutes: 35,
      prev_temp_day: 65, temp_day: 63, prev_pressure: 1014, pressure: 1019,
      weekday: 0, is_rainy: false,
    },
  ]), []);

  const WeatherIcon = (type) => {
    switch (type) {
      case 'cloudy': return WiCloudy;
      case 'partly_cloudy': return WiDayCloudy;
      case 'rainy': return WiRain;
      case 'sun':
      default: return WiDaySunny;
    }
  };

  const cardStyle = { minWidth: 240, flex: '0 0 auto', borderRadius: 12, border: '1px solid var(--surface-border,#e5e7eb)', background: 'var(--surface-elevated,#fff)', padding: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' };
  const scoreBarWrap = { height: 8, background: 'var(--surface-muted,#f3f4f6)', borderRadius: 999, overflow: 'hidden' };
  const scoreBar = (pct) => ({ width: `${pct}%`, height: '100%', background: pct >= 60 ? 'var(--accent,#16a34a)' : pct >= 30 ? '#f59e0b' : '#ef4444' });
  const labelMuted = { fontSize: 12, color: 'var(--text-muted,#6b7280)' };

  if (loading) return <div className="p-3">Loading…</div>;
  if (error) return <div className="p-3 text-danger">{error}</div>;
  if (!weights) return <div className="p-3">No settings.</div>;

  return (
    <div className="container py-3" data-testid="forecast-settings-page">
      <h4 className="mb-3">Hunt Forecast Settings</h4>
      <div className="row">
        <div className="col-lg-6 col-md-12 mb-4">
          <div className="p-3 border rounded-3">
            <div className="mb-2" style={{ fontWeight: 700 }}>Base Weights</div>
            <Slider label="Lunar" min={0} max={1} step={0.01} value={numberOrNull(weights.lunar) ?? 0} onChange={updateField('lunar')} format={formatPercent} />
            <Slider label="Temperature" min={0} max={1} step={0.01} value={numberOrNull(weights.temp) ?? 0} onChange={updateField('temp')} format={formatPercent} />
            <Slider label="Pressure" min={0} max={1} step={0.01} value={numberOrNull(weights.pressure) ?? 0} onChange={updateField('pressure')} format={formatPercent} />
            <Slider label="Wind" min={0} max={1} step={0.01} value={numberOrNull(weights.wind) ?? 0} onChange={updateField('wind')} format={formatPercent} />
          </div>
        </div>
        <div className="col-lg-6 col-md-12 mb-4">
          <div className="p-3 border rounded-3">
            <div className="mb-2" style={{ fontWeight: 700 }}>Bonuses</div>
            <Slider label="Daylight Major Bonus" min={0} max={0.5} step={0.01} value={numberOrNull(weights.major_bonus) ?? 0} onChange={updateField('major_bonus')} format={formatPoints} />
            <Slider label="Major + Minor Bonus" min={0} max={0.5} step={0.01} value={numberOrNull(weights.combo_bonus) ?? 0} onChange={updateField('combo_bonus')} format={formatPoints} />
            <Slider label="Sunrise/Sunset Major Bonus" min={0} max={0.5} step={0.01} value={numberOrNull(weights.crepuscular_major_bonus) ?? 0} onChange={updateField('crepuscular_major_bonus')} format={formatPoints} />
            <Slider label="Sunrise/Sunset Minor Bonus" min={0} max={0.5} step={0.01} value={numberOrNull(weights.crepuscular_minor_bonus) ?? 0} onChange={updateField('crepuscular_minor_bonus')} format={formatPoints} />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 col-md-12 mb-4">
          <div className="p-3 border rounded-3">
            <div className="mb-2" style={{ fontWeight: 700 }}>Final Adjustments</div>
            <Slider label="Midweek Bonus" min={0} max={0.3} step={0.01} value={numberOrNull(weights.midweek_bonus_final) ?? 0} onChange={updateField('midweek_bonus_final')} format={formatPoints} />
            <Slider label="Weekend Penalty" min={0} max={0.3} step={0.01} value={numberOrNull(weights.weekend_penalty_final) ?? 0} onChange={updateField('weekend_penalty_final')} format={formatPoints} />
            <Slider label="Cool Day Bonus" min={0} max={0.3} step={0.01} value={numberOrNull(weights.cool_day_bonus_final) ?? 0} onChange={updateField('cool_day_bonus_final')} format={formatPoints} />
            <Slider label="Rain Penalty" min={0} max={0.5} step={0.01} value={numberOrNull(weights.rain_penalty_final) ?? 0} onChange={updateField('rain_penalty_final')} format={formatPoints} />
            <Slider label="Optimal Wind Match Bonus" min={0} max={0.3} step={0.01} value={numberOrNull(weights.optimal_wind_match_bonus_final) ?? 0} onChange={updateField('optimal_wind_match_bonus_final')} format={formatPoints} />
            <div className="mt-2">
              <label className="form-label mb-1" style={{ fontWeight: 600 }}>Cool Day Threshold (°F)</label>
              <div className="d-flex align-items-center gap-2">
                <input type="range" className="form-range" min={30} max={80} step={1} value={numberOrNull(weights.cool_day_threshold_f) ?? 60} onChange={(e) => updateField('cool_day_threshold_f')(Number(e.target.value))} />
                <span>{Math.round(numberOrNull(weights.cool_day_threshold_f) ?? 60)}°F</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Example Days Preview */}
      <div className="mt-4">
        <div className="mb-2" style={{ fontWeight: 700 }}>Example Days (Preview)</div>
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
          {scenarios.map((s) => {
            const score = computePreviewScore(weights, s);
            const Icon = WeatherIcon(s.weather_type);
            const windCard = getCardinalFromDegrees(s.wind_deg || 0);
            return (
              <div key={s.key} style={cardStyle} aria-label={`Example card ${s.title}`}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{s.title}</div>
                <div style={scoreBarWrap}><div style={scoreBar(score)} /></div>
                <div className="d-flex justify-content-between" style={{ marginTop: 6 }}>
                  <span style={labelMuted}>Score</span>
                  <span style={{ fontWeight: 600 }}>{score}</span>
                </div>
                <div className="d-flex align-items-center gap-2" style={{ marginTop: 8 }}>
                  <Icon size={20} aria-hidden="true" />
                  <span style={{ ...labelMuted, textTransform: 'capitalize' }}>{s.weather_type.replace('_',' ')}</span>
                </div>
                <div className="d-flex align-items-center gap-2" style={{ marginTop: 4 }}>
                  <WiThermometer size={18} aria-hidden="true" />
                  <span>{Math.round(s.temp_max)}° / {Math.round(s.temp_min)}°</span>
                </div>
                <div style={{ marginTop: 4 }}>
                  <span style={labelMuted}>Wind</span>{' '}
                  <span>{Math.round(s.wind_speed)} mph {windCard}</span>
                </div>
                <div style={{ marginTop: 8 }}>
                  <div style={labelMuted}>Daylight Periods</div>
                  <div>{(s.majors_daylight_count||0)} major{(s.majors_daylight_count||0)===1?'':'s'}, {(s.minors_daylight_count||0)} minor{(s.minors_daylight_count||0)===1?'':'s'}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="d-flex gap-2">
        <button type="button" className="y-btn y-btn-secondary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        <button type="button" className="y-btn y-btn-ghost" onClick={load} disabled={saving}>Reset</button>
      </div>
    </div>
  );
}


